import { Data, DateUtils, Misc, ModalUtils, Model, NeoModel } from '@singularsystems/neo-core';
import { Routing, Views } from '@singularsystems/neo-react';
import { NotificationDuration } from '../../../App/Models/NotificationDuration';
import { AppService, DomainTypes, Types } from '../../DomainTypes';
import BranchDetailLookup from '../../Models/Catalogue/Reporting/Lookups/BranchDetailLookup';
import Investment from '../../Models/Investment/Investment';
import NewInvestorInvestmentCommand from '../../Models/InvestorInvestments/Commands/NewInvestorInvestmentCommand';
import InvestorInvestment from '../../Models/InvestorInvestments/InvestorInvestment';
import InvestorInvestmentPagedLookup from '../../Models/InvestorInvestments/Lookups/InvestorInvestmentPagedLookup';
import InvestorInvestmentPagedLookupCriteria from '../../Models/InvestorInvestments/Lookups/InvestorInvestmentPagedLookupCriteria';
import NoteVM from '../../Models/Notes/NoteVM';
import InvestorVM from '../Investor/InvestorVM';
import NoticeComponentVM from "./Components/NoticeComponentVM";
import { ReportingTypes, IReport } from "@singularsystems/neo-reporting";
import AuditConfirmationReport from "../../../Reporting/Reports/AuditConfirmation/AuditConfirmationReport";
import { SummaryType } from "../../../Reporting/Reports/AuditConfirmation/AuditConfirmationCriteria";
import TaxConfirmationReport from "../../../Reporting/Reports/TaxConfirmation/TaxConfirmationReport";
import AuthorisationVM from "../../Services/AuthorisationVM";

@NeoModel
export default class InvestorInvestmentVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public authorisation = AppService.get(Types.Neo.Security.AuthorisationService),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        public investorInvestmentsApiClient = AppService.get(DomainTypes.ApiClients.InvestorInvestment),
        public investorApiClient = AppService.get(DomainTypes.ApiClients.Investor),
        public reportingApiClient = AppService.get(DomainTypes.ApiClients.CatalogueReporting),
        public pipelineApiClient = AppService.get(DomainTypes.ApiClients.CataloguePipeline),
        public issuerInvestmentApiClient = AppService.get(DomainTypes.ApiClients.IssuerInvestment),
        public investmentApiClient = AppService.get(DomainTypes.ApiClients.Investment),
        public reportingService = AppService.get(ReportingTypes.Services.ReportingService)) {

        super(taskRunner);

        this.searchAutoRunner = this.criteria.onAnyPropertyChanged(() => this.pageManager.refreshData());
    }

    public noteVM = this.registerViewModel(NoteVM);
    public authorisationVM = this.registerViewModel(AuthorisationVM);
    public noticesVM = this.registerViewModel(new NoticeComponentVM(this));

    public searchAutoRunner: Misc.IAutoRunner;

    public criteria = new InvestorInvestmentPagedLookupCriteria();
    public pageManager = new Data.PageManager(this.criteria, InvestorInvestmentPagedLookup, this.investorInvestmentsApiClient.getInvestorInvestmentsPaged, 
        { fetchInitial: true, sortBy: "investorInvestmentId" });

    public investorInvestment: InvestorInvestment | null = null;
    
    public newInvestorInvestment: NewInvestorInvestmentCommand | null = null;

    public issuerInvestmentName = "";

    public branchDataSource = new Data.ApiClientLookupDataSource(this.reportingApiClient.getBranchDetailLookups, BranchDetailLookup);

    public selectedTab = "Details";

    public async initialise() {

    }

    public async setCriteriaInvestor(investorId: number | null = null)
    {
        if (investorId === InvestorVM.lastInvestor?.investorId) {
            this.criteria.investorId = investorId;
            this.criteria.investorName = InvestorVM.lastInvestor.investorName;
            // Initial fetch will still happen on render.
            this.searchAutoRunner.cancelPending();
        }
    }

    public async newInvestment() {
        this.onInvestmentChanged();

        const command = new NewInvestorInvestmentCommand();

        if (this.criteria.investorId) {
            const investor = await this.taskRunner.waitForData(this.investorApiClient.getInvestor(this.criteria.investorId));
            command.setInvestor(investor);
        }

        this.newInvestorInvestment = command;
        this.selectedTab = "Details";
    }

    public async loadInvestment(viewParam: Routing.IViewParameter) {
        this.onInvestmentChanged();

        const data = await this.taskRunner.waitForData(this.investorInvestmentsApiClient.getInvestorInvestment(viewParam.value as number));
        this.investorInvestment = InvestorInvestment.fromJSObject<InvestorInvestment>(data);
        this.selectedTab = "Investment";

        viewParam.description = `${data.refNo} - ${data.lookup!.investorName}`;
    }

    private onInvestmentChanged() {
        this.noticesVM.reset();
    }

    public async createNewInvestment() {
        if (this.newInvestorInvestment) {

            const investorInvestment = InvestorInvestment.fromCreateCommand(this.newInvestorInvestment);

            if (this.newInvestorInvestment.investmentId) {
                const investmentData = await this.taskRunner.waitForData(this.investmentApiClient.getInvestment(this.newInvestorInvestment.investmentId));
                investorInvestment.investment = Investment.fromExistingInvestment(investmentData);
                if (investorInvestment.lookup.entity) {
                    investorInvestment.investment.completeDividendCycle = investorInvestment.lookup.entity.investorsCompleteDividendCycle;
                }
            }

            investorInvestment.investment.lookup.minimumTransactionAmount = investorInvestment.lookup.entity?.minimumTransactionAmount ?? 0;

            if (investorInvestment.issuerInvestmentId )
            {
                const data = await this.taskRunner.waitFor(this.issuerInvestmentApiClient.getIssuerInvestment(investorInvestment.issuerInvestmentId));
                investorInvestment.investorInvestmentDetail.certificateNo = data.data.issuerInvestmentDetail.certificateNumber;
                investorInvestment.investorInvestmentDetail.distinctiveNo = data.data.issuerInvestmentDetail.distinctiveNumber;
                if (data.data.lastAllocationDate) {
                    investorInvestment.lookup.issuerInvestmentAllocationDate = new Date(data.data.lastAllocationDate);
                }
            }

            this.investorInvestment = investorInvestment;
            this.newInvestorInvestment = null;
        }
    }

    public async saveInvestment() {
        await this.taskRunner.run(async () => {
            if (this.investorInvestment) {
                const dateCycleIsNew = this.investorInvestment.investment.dividendCycle?.isNew;
                const response = await this.investorInvestmentsApiClient.saveInvestorInvestment(this.investorInvestment.toJSObject());
                this.investorInvestment.investment.afterSave(response.data);
                this.investorInvestment.update(response.data.investment);
                this.pageManager.refreshPage();

                if (dateCycleIsNew) {
                    this.dataCache.dateCycles.expire();
                }

                this.notifications.addSuccess("Save", "Investment saved successfully.", NotificationDuration.Standard);
            }
        });
    }

    public setInvestorInvestmentPendingDelete(investorInvestment: InvestorInvestment) {
        investorInvestment.isPendingDelete = !investorInvestment.isPendingDelete;
        if (investorInvestment.isPendingDelete) {
            this.notifications.addInfo("Mark Deleted", `Please click save to action this request.`, NotificationDuration.Standard);
        }
    }

    public reportInfo: { name: string, report: IReport } | null = null;

    public async showAuditConfirmationReportCriteria(isDistributed: boolean) {
        const report = await this.taskRunner.waitFor(
            this.reportingService.createReport(
                AuditConfirmationReport,
                { 
                    type: isDistributed ? SummaryType.Distributed : SummaryType.Accrued,
                    investorInvestmentId: this.investorInvestment!.investorInvestmentId 
                })) as IReport;
                
        this.showReportCriteria(report);
    }

    public async showTaxConfirmationReportCriteria() {

        const report = await this.taskRunner.waitFor(
            this.reportingService.createReport(
                TaxConfirmationReport,
                {
                    investorInvestmentId: this.investorInvestment!.investorInvestmentId 
                })) as IReport;
                
        this.showReportCriteria(report);
    }

    private showReportCriteria(report: IReport) {
        this.reportInfo = { name: report.reportInfo.displayName, report: report };
    }

    public viewReport() {
        if (this.reportInfo) {
            this.reportingService.viewReportModal(this.reportInfo.report);
            this.reportInfo = null;
        }
    }
}